import ObjectModelPermissionPermittee from "~/models/objectModelPermissionPermittee";
import {ObjectModelPermissionCapabilityType} from "~/enums/objectModelPermissionCapabilityType";
import ObjectModelPermission from "~/models/objectModelPermissions/objectModelPermission";
import {Logger} from "~/services/logger";
import {IPermittablesContext, ObjectModelPermissionPermittableType} from "~/interfaces/objectModelInterfaces";
import {ObjectModelPermissionDefinition} from "~/models/objectModels/objectModelPermissionDefinition";

export default {
  data: (): object => {
    return {
      ObjectModelPermissionCapabilityType,
    };
  },
  created() {
    if (!this.permissionType) {
      throw new Error('permissionType() computed property must be defined in component.');
    }
  },
  computed: {
    permissionTypeCapabilityTypes(): Array<ObjectModelPermissionCapabilityType> {
      return ObjectModelPermissionDefinition.capabilityTypesFor(this.permissionType);
    },
  },
  methods: {
    /**
     * If Permission found, destroy; else, create Permission. Functions as toggle.
     */
    createDestroyPermission(permittee: ObjectModelPermissionPermittee, permittable: ObjectModelPermissionPermittableType, capabilityType: ObjectModelPermissionCapabilityType): void {
      this.ifFoundElseCreatePermission(permittee, permittable, capabilityType, found => found.destroy());
      this.selectedObjectModel.saveChangesToStore();
    },
    /**
     * If Permission found, done; else, create Permission.
     */
    findOrCreatePermission(permittee: ObjectModelPermissionPermittee, permittable: ObjectModelPermissionPermittableType, capabilityType: ObjectModelPermissionCapabilityType): void {
      this.ifFoundElseCreatePermission(permittee, permittable, capabilityType);
    },
    /**
     * Find and return Permission
     */
    permissionFor(permittee: ObjectModelPermissionPermittee, permittable: ObjectModelPermissionPermittableType, capabilityType: ObjectModelPermissionCapabilityType): ObjectModelPermission {
      if (!permittee || !permittable || !capabilityType) {
        Logger.errorOnce('ObjectModelPermissionMixin#permissionFor', 'Must include all arguments in function call!');
        return;
      }
      return this.selectedObjectModel.permissionsArray.find(permission => {
        return permission.permittee_client_id === permittee.client_id &&
          permission.permittable_client_id === permittable.client_id &&
          permission.permission_type === this.permissionType &&
          permission.capability_type === capabilityType;
      });
    },
    /**
     * Toggle all Permissions of this.permissionType for permittee
     */
    toggleAllPermissionsFor(permittee, context?: IPermittablesContext): void {
      let callback: (permittable) => any;
      if (this.allPermissionsEnabledFor(permittee, context)) {
        callback = permittable => {
          this.permissionTypeCapabilityTypes.forEach(capabilityType => {
            this.permissionFor(permittee, permittable, capabilityType)?.destroy();
          });
        };
      } else {
        callback = permittable => {
          this.permissionTypeCapabilityTypes.forEach(capabilityType => {
            this.findOrCreatePermission(permittee, permittable, capabilityType);
          });
        };
      }
      this.forEachPermittable(callback, context);
      this.selectedObjectModel.saveChangesToStore();
    },
    allPermissionsEnabledFor(permittee: ObjectModelPermissionPermittee, context?: IPermittablesContext): boolean {
      const tracker = this.trackPermissionsFor(permittee, context);
      return tracker.someEnabled && !tracker.someDisabled;
    },
    somePermissionsEnabledFor(permittee: ObjectModelPermissionPermittee, context?: IPermittablesContext): boolean {
      const tracker = this.trackPermissionsFor(permittee, context);
      return tracker.someEnabled && tracker.someDisabled;
    },
    /* Helper Methods */
    /**
     * If Permission found, call foundCallback; else, create Permission.
     */
    ifFoundElseCreatePermission(permittee: ObjectModelPermissionPermittee, permittable: ObjectModelPermissionPermittableType, capabilityType: ObjectModelPermissionCapabilityType, foundCallback?: (found: ObjectModelPermission) => any): void {
      if (!permittee || !permittable || !capabilityType) {
        Logger.errorOnce('ObjectModelPermissionMixin#ifFoundElseCreatePermission', 'Must include all arguments in function call!');
        return;
      }

      const found = this.permissionFor(permittee, permittable, capabilityType);
      if (found) {
        foundCallback && foundCallback(found);
      } else {
        this.selectedObjectModel.createPermission(permittee, permittable, this.permissionType, capabilityType);
      }
    },
    /**
     * tracks if some Permissions exist and some do not exist for permittee and this.permissionType
     */
    trackPermissionsFor(permittee: ObjectModelPermissionPermittee, context?: IPermittablesContext): {someEnabled: boolean, someDisabled: boolean} {
      const tracker = {someEnabled: false, someDisabled: false};
      this.forEachPermittable(permittable => {
        this.permissionTypeCapabilityTypes.forEach(capabilityType => {
          if (tracker.someEnabled && tracker.someDisabled || !ObjectModelPermissionDefinition.canCreate({permittee, permittable})) {
            // skip checks for permissions that can't be changed
            Logger.debug('trackPermissionsFor', permittee.display, '-', permittable.label_title, 'not found or skipping')
          } else {
            const present = !!this.permissionFor(permittee, permittable, capabilityType);
            Logger.debug('trackPermissionsFor', permittee.display, '-', permittable.label_title, 'present:', present)
            present === true ? tracker.someEnabled = true : tracker.someDisabled = true;
          }
        });
      }, context);
      return tracker;
    },
    forEachPermittable(callback: (action) => any, context?: IPermittablesContext): void {
      this.selectedObjectModel.permittablesFor(this.permissionType, context).forEach(callback);
    },
  }
}